import { formatShortDateWithWeekday, formatTime } from "@clipboard-health/date-time";
import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import BoltIcon from "@mui/icons-material/Bolt";
import { Chip, Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import pluralize from "pluralize";

import { type ShiftBlockWithMappedDetails } from "../../redesign/ShiftDiscovery/Blocks/api/useFetchPaginatedShiftBlocks";
import {
  getFinalPayForShiftBlock,
  getHourlyPayForShiftBlock,
  getHumanReadableShiftDuration,
} from "../../redesign/ShiftDiscovery/Blocks/utils";
import { LunchBreakInformationIconAndDialog } from "./LunchBreakInformationIconAndDialog";

interface ShiftBlockHeaderProps {
  shiftBlockWithDetails: ShiftBlockWithMappedDetails;
  agentTimezone: string;
  mandatoryBreakDurationInMinutes: number;
  showStartEndTime?: boolean;
}

export function ShiftBlockHeader(props: ShiftBlockHeaderProps) {
  const {
    shiftBlockWithDetails,
    agentTimezone,
    mandatoryBreakDurationInMinutes,
    showStartEndTime = true,
  } = props;
  const { blockStartTime, blockEndTime, facility, shifts, agentReq, orientationBlockId } =
    shiftBlockWithDetails;
  const finalPay = getFinalPayForShiftBlock(shiftBlockWithDetails);
  // eslint-disable-next-line prefer-destructuring
  const firstShiftDetails = shifts[0];
  const shiftsLength = shifts.length;
  const lastShiftDetails = shifts[shiftsLength - 1];
  const hourlyPay = getHourlyPayForShiftBlock(shiftBlockWithDetails);
  // If hourly pay is different for at least one shift, show the approximate pay symbol
  const showApproximatePaySymbol = shifts.some((shift) => {
    const shiftPay = shift.offer?.pay?.value ?? shift.pay;
    return shiftPay !== hourlyPay;
  });

  const isOrientationBlock = isDefined(orientationBlockId);

  return (
    <Stack spacing={0.25}>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
        <Text sx={{ color: (theme) => theme.palette.success.main, fontWeight: 600 }} variant="h3">
          {formatDollarsAsUsd(finalPay, { minimumFractionDigits: 2 })} total
        </Text>

        <Text
          variant="h3"
          sx={{
            opacity: 0.6,
          }}
        >
          {shifts.length} {pluralize("shift", shifts.length)}
        </Text>
      </Stack>

      {showStartEndTime && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Text>
            {formatTime(blockStartTime, { timeZone: agentTimezone })} -{" "}
            {formatTime(blockEndTime, { timeZone: agentTimezone })}
          </Text>

          <Stack direction="row" alignItems="center">
            <Text
              sx={{
                opacity: 0.6,
              }}
            >
              {getHumanReadableShiftDuration({
                shiftDuration: firstShiftDetails.time,
                withLunchBreak: false,
                mandatoryBreakDurationInMinutes,
              })}
            </Text>
            <LunchBreakInformationIconAndDialog
              shiftDetails={{
                facilityName: facility.name,
                shiftTime: firstShiftDetails.time,
                mandatoryBreakDurationInMinutes,
              }}
            />
          </Stack>
        </Stack>
      )}

      <Text>
        {formatShortDateWithWeekday(firstShiftDetails.start, { timeZone: agentTimezone })} -{" "}
        {formatShortDateWithWeekday(lastShiftDetails.start, {
          timeZone: agentTimezone,
        })}
      </Text>

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Text
          sx={{
            color: (theme) => theme.palette.success.main,
            fontWeight: 600,
          }}
        >
          {showApproximatePaySymbol ? "~" : ""}
          {formatDollarsAsUsd(hourlyPay, { minimumFractionDigits: 2 })} / hr
        </Text>

        {firstShiftDetails.isInstantPay && (
          <Stack direction="row">
            <BoltIcon sx={{ color: (theme) => theme.palette.success.main }} />
            <Text bold sx={{ color: (theme) => theme.palette.success.main, fontWeight: 600 }}>
              InstantPay
            </Text>
          </Stack>
        )}
      </Stack>
      <Stack spacing={0.5} alignContent="start" direction="row">
        <Chip size="small" label={agentReq} />
        {isOrientationBlock && <Chip size="small" label="Orientation Block" />}
      </Stack>
    </Stack>
  );
}
